import type * as XLSXNS from "xlsx";

/**
 * EXCEL SAYFASI → EKRAN IZGARASI.
 *
 * NEDEN AYRI MODÜL: önizleme "Excel'deki gibi görünsün" isteniyor (kullanıcı, 29 Ağu 2026) —
 * yani sütun genişlikleri, birleştirilmiş hücreler ve satır yükseklikleri dosyadan gelmeli.
 * Bu dönüşüm saf bir fonksiyondur; bileşenin içinde kalsaydı test edilemezdi.
 *
 * ⚠ Sütun/satır ölçüleri YALNIZ `XLSX.read(..., { cellStyles: true })` ile okunduğunda gelir
 * (`!cols` / `!rows`). Bayrak unutulursa her sütun varsayılan genişlikte çıkar — sessizce
 * "Excel gibi değil" olur; çağıran taraf bu yüzden bayrağı geçmek zorunda.
 */

export interface Hucre {
  metin: string;
  /** Sayısal hücre → Excel gibi SAĞA yaslanır. */
  sayisal: boolean;
  rowSpan: number;
  colSpan: number;
  /** Birleştirmenin ÜSTÜNÜ örttüğü hücre — DOM'a hiç basılmaz. */
  ortulu: boolean;
}

export interface Izgara {
  satirlar: Hucre[][];
  /** Aralığın başladığı gerçek Excel satır/sütun dİZİNİ (0 tabanlı) — başlık etiketleri (1,2,3 / A,B,C) bundan türetilir. */
  ilkSatir: number;
  ilkSutun: number;
  /** Piksel cinsinden sütun genişlikleri — `<colgroup>` bunları kullanır. */
  sutunGenislik: number[];
  /** Piksel cinsinden satır yükseklikleri. */
  satirYukseklik: number[];
  /** Doğal toplam genişlik (px) — "sayfaya sığdır" ölçeği bundan hesaplanır. */
  toplamGenislik: number;
  /** Tavan aşıldıysa gerçek boyut; aşılmadıysa null. */
  kirpildi: { satir: number; sutun: number } | null;
}

/** Tarayıcıyı dondurmamak için tavan — aşılırsa çağıran BİLDİRİR. */
export const MAX_SATIR = 500;
export const MAX_SUTUN = 60;

const VARSAYILAN_SUTUN_PX = 64;
/** Excel'in varsayılan satır yüksekliği: 15 punto = 20 px (96 DPI). */
const VARSAYILAN_SATIR_PX = 20;
/** Calibri 11'in en geniş rakamı 96 DPI'da 7 px — Excel sütun genişliğini bununla ölçer. */
const KARAKTER_PX = 7;

/**
 * Sütun genişliği → piksel.
 *
 * ÖNCE `wch` (Excel'in KENDİ birimi: karakter), sonra `wpx`. Sıra bilinçli ve ölçüldü
 * (29 Ağu 2026): SheetJS `wpx`i kendisi türetirken en geniş rakamı 6 px sayıyor (`MDW: 6`),
 * Excel ise 7 px. `wch: 8` bir sütun SheetJS'te 53 px, Excel'de ~62 px çıkıyordu — yani
 * `wpx`e güvenmek tabloyu Excel'dekinden dar gösteriyordu. Gerçek xlsx dosyaları genişliği
 * zaten karakter cinsinden saklar; `wpx` yalnız bizim px yazdığımız durumlarda anlamlıdır.
 */
function sutunPx(c: XLSXNS.ColInfo | undefined): number {
  if (!c) return VARSAYILAN_SUTUN_PX;
  if (c.hidden) return 0;
  if (typeof c.wch === "number" && c.wch > 0) return Math.round(c.wch * KARAKTER_PX + 5);
  if (typeof c.wpx === "number" && c.wpx > 0) return Math.round(c.wpx);
  return VARSAYILAN_SUTUN_PX;
}

/**
 * Sayfayı ızgaraya çevirir.
 *
 * KIRPMA: dosyanın `!ref` aralığı çoğu zaman gerçek veriden GENİŞTİR (Excel boş sütunları
 * aralığa katar). Sondaki tamamen boş satır/sütunlar atılır — yoksa önizlemede 20 satır
 * boşluk görünür (29 Ağu 2026 ekran görüntüsündeki durum).
 */
export function sayfayiIzgaraCevir(XLSX: typeof XLSXNS, ws: XLSXNS.WorkSheet): Izgara {
  const bos: Izgara = { satirlar: [], ilkSatir: 0, ilkSutun: 0, sutunGenislik: [], satirYukseklik: [], toplamGenislik: 0, kirpildi: null };
  const ref = ws["!ref"];
  if (!ref) return bos;
  const aralik = XLSX.utils.decode_range(ref);

  const hamSatir = aralik.e.r - aralik.s.r + 1;
  const hamSutun = aralik.e.c - aralik.s.c + 1;

  /** Hücrenin EKRANDA görünecek metni: `w` (Excel biçimlisi) → yoksa ham değer. */
  const metinAl = (r: number, c: number): { metin: string; sayisal: boolean } => {
    const h = ws[XLSX.utils.encode_cell({ r, c })] as XLSXNS.CellObject | undefined;
    if (!h) return { metin: "", sayisal: false };
    const metin = h.w != null ? String(h.w) : h.v != null ? String(h.v) : "";
    return { metin, sayisal: h.t === "n" };
  };

  // ── Sondaki boş satır/sütunları kırp ──
  let sonSatir = -1, sonSutun = -1;
  for (let r = 0; r < hamSatir; r++) {
    for (let c = 0; c < hamSutun; c++) {
      if (metinAl(aralik.s.r + r, aralik.s.c + c).metin !== "") {
        if (r > sonSatir) sonSatir = r;
        if (c > sonSutun) sonSutun = c;
      }
    }
  }
  // Birleştirmeler veri taşımasa bile alanı tanımlar — kırpma onları da kapsamalı.
  for (const m of (ws["!merges"] ?? [])) {
    const r = m.e.r - aralik.s.r, c = m.e.c - aralik.s.c;
    if (r >= 0 && r < hamSatir && r > sonSatir && metinAl(m.s.r, m.s.c).metin !== "") sonSatir = r;
    if (c >= 0 && c < hamSutun && c > sonSutun && metinAl(m.s.r, m.s.c).metin !== "") sonSutun = c;
  }
  if (sonSatir < 0 || sonSutun < 0) return bos;

  const gercekSatir = sonSatir + 1;
  const gercekSutun = sonSutun + 1;
  const nSatir = Math.min(gercekSatir, MAX_SATIR);
  const nSutun = Math.min(gercekSutun, MAX_SUTUN);

  // ── Birleştirme haritası: çapa hücreye span, örtülenlere bayrak ──
  const span = new Map<string, { rs: number; cs: number }>();
  const ortulu = new Set<string>();
  for (const m of (ws["!merges"] ?? [])) {
    const r0 = m.s.r - aralik.s.r, c0 = m.s.c - aralik.s.c;
    const r1 = m.e.r - aralik.s.r, c1 = m.e.c - aralik.s.c;
    if (r0 < 0 || c0 < 0 || r0 >= nSatir || c0 >= nSutun) continue;
    // Tavanla kırpılmış alana taşan birleştirme, tabloyu bozmasın diye kısaltılır.
    const rs = Math.min(r1, nSatir - 1) - r0 + 1;
    const cs = Math.min(c1, nSutun - 1) - c0 + 1;
    if (rs > 1 || cs > 1) span.set(`${r0}:${c0}`, { rs, cs });
    for (let r = r0; r <= Math.min(r1, nSatir - 1); r++) {
      for (let c = c0; c <= Math.min(c1, nSutun - 1); c++) {
        if (r !== r0 || c !== c0) ortulu.add(`${r}:${c}`);
      }
    }
  }

  const cols = (ws["!cols"] ?? []) as XLSXNS.ColInfo[];
  const rows = (ws["!rows"] ?? []) as XLSXNS.RowInfo[];
  const sutunGenislik: number[] = [];
  for (let c = 0; c < nSutun; c++) sutunGenislik.push(sutunPx(cols[aralik.s.c + c]));
  const satirYukseklik: number[] = [];
  for (let r = 0; r < nSatir; r++) {
    const ri = rows[aralik.s.r + r];
    satirYukseklik.push(ri?.hidden ? 0 : typeof ri?.hpx === "number" && ri.hpx > 0 ? Math.round(ri.hpx) : VARSAYILAN_SATIR_PX);
  }

  const satirlar: Hucre[][] = [];
  for (let r = 0; r < nSatir; r++) {
    const satir: Hucre[] = [];
    for (let c = 0; c < nSutun; c++) {
      const anahtar = `${r}:${c}`;
      const { metin, sayisal } = metinAl(aralik.s.r + r, aralik.s.c + c);
      const sp = span.get(anahtar);
      satir.push({ metin, sayisal, rowSpan: sp?.rs ?? 1, colSpan: sp?.cs ?? 1, ortulu: ortulu.has(anahtar) });
    }
    satirlar.push(satir);
  }

  /**
   * EXCEL TAŞMASI: bir metin hücresi, sağındaki hücreler BOŞSA oraya taşar (Excel'in
   * kendi davranışı — başlık satırları böyle görünür). Boş komşuları `colSpan` ile
   * yutarak taklit ediyoruz: sütun genişlikleri ve toplam genişlik DEĞİŞMEZ, ama metin
   * kırpılmaz. Yapılmazsa dar bir sütundaki uzun başlık "..." ile kesilir — Excel'de kesilmez.
   *
   * `rowSpan > 1` olan hücre taşmaz: alttaki satırlarda karşılık gelen hücreler "örtülü"
   * işaretlenmediği için satır hücre sayısı kayar, tablo bozulurdu.
   */
  for (const satir of satirlar) {
    for (let c = 0; c < nSutun; c++) {
      const h = satir[c]!;
      if (h.ortulu || h.sayisal || h.metin === "" || h.colSpan > 1 || h.rowSpan > 1) continue;
      let k = c + 1;
      while (k < nSutun && !satir[k]!.ortulu && satir[k]!.metin === "") {
        satir[k]!.ortulu = true;
        h.colSpan++;
        k++;
      }
    }
  }

  return {
    satirlar,
    ilkSatir: aralik.s.r,
    ilkSutun: aralik.s.c,
    sutunGenislik,
    satirYukseklik,
    toplamGenislik: sutunGenislik.reduce((a, b) => a + b, 0),
    kirpildi: gercekSatir > MAX_SATIR || gercekSutun > MAX_SUTUN ? { satir: gercekSatir, sutun: gercekSutun } : null,
  };
}
